<template>
  <suite-modal
      :show="showSessionModal"
      :title="resolvedTitle"
      type="warning"
  >
    <template #content>
      <div v-if="refreshingSession" class="loading-state">
        <ion-spinner name="crescent" class="custom-spinner"></ion-spinner>
        <p>{{ $t('auth.loading_default') }}</p>
      </div>
      <p v-else v-html="resolvedBodyContent"></p>
    </template>

    <template v-if="!refreshingSession && modalType !== 'warning'" #buttons>
      <suite-button @click="handleRefreshSession" color="primary">
        {{ lastErrorKey ? $t('common.retryAction') : $t('auth.extendSession') }}
      </suite-button>

      <suite-button @click="handleLogoutManual" fill="outline">
        {{ $t('common.logout') }}
      </suite-button>
    </template>
  </suite-modal>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { IonSpinner } from '@ionic/vue';

import { useAuth } from "@/composables/useAuth.ts";
import { useBiometric } from "@/composables/useBiometric.ts";
import { useNats } from "@/composables/useNats.ts";
import { closeSessionModal } from "@/utils/notifications.ts";

import SuiteModal from "@/components/SuiteModal.vue";
import SuiteButton from "@/components/SuiteButton.vue";

import { isNative } from "@/utils/platform.ts";

const { t } = useI18n();
const { silentAuthenticate } = useBiometric();
const { manageTracker } = useNats();

const {
  isLoggedIn,
  user,
  showSessionModal,
  timeLeft,
  modalType,
  lastErrorKey,
  refreshingSession,
  executeRefreshSession,
  logout,
} = useAuth();

const resolvedTitle = computed(() => {
  if (lastErrorKey.value) {
    return t('auth.errors.refreshSessionError');
  }
  if (modalType.value === 'warning') {
    return t('auth.noticeBeforeDisconnection');
  }
  return t('auth.sessionExpiredTitle');
});

const resolvedBodyContent = computed(() => {
  if (lastErrorKey.value) {
    const errorKey = `auth.errors.${lastErrorKey.value}`;
    const translation = t(errorKey);

    if (translation === errorKey) {
      return t('auth.errors.generic_error');
    }
    return translation;
  }

  if (modalType.value === 'warning') {
    return t('auth.activityModalBody', { timeleft: timeLeft.value, br: '<br>' });
  }

  return t('auth.sessionExpiredBody');
});

const handleRefreshSession = async () => {
  const performRefresh = async () => {
    const result = await executeRefreshSession(false);

    if (result.success) {
      if (isLoggedIn.value && user.value) {
        manageTracker(user.value.Id, user.value.TokenExp, true, true);
      }
      lastErrorKey.value = null;
      await closeSessionModal();
    } else {
      modalType.value = 'expired';
      lastErrorKey.value = result.errorKey || 'generic_error';
    }
  };

  if (isNative) {
    await silentAuthenticate(async () => {
      if (isLoggedIn.value && user.value) {
        manageTracker(user.value.Id, user.value.TokenExp, true, true);
        await closeSessionModal();
      }
    });
  } else {
    await performRefresh();
  }
};

const handleLogoutManual = async () => {
  await logout(null, null);
};
</script>